import { Component,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import L from 'leaflet';
import { ArivoBottomSheetDialogComponent } from '@shared/components/ui-elements';
import { ActivatedRoute, Router } from '@angular/router';
import { UrlPipe } from '@core/pipes';
import { DecimalPipe } from '@angular/common';
import { TypedRoute } from '@shared/utils';
import { Garage, ProductsResolve } from '@shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Language } from '@core/utils';
import { FeatureFlagService, LanguageService } from '@core/services';

interface LatLng {
  lat: number;
  lng: number;
}

interface MapMarker extends Garage {
  marker: L.Marker;
}

@Component({
  selector: 'app-public-products',
  templateUrl: './public-products.component.html',
  styleUrl: './public-products.component.scss',
})
export class PublicProductsComponent implements OnInit, OnDestroy {
  map?: L.Map;
  display: 'map' | 'list' = 'map';

  mapMarkers: MapMarker[] = [];
  filteredMapMarkers: MapMarker[] = [];
  markerFilter: string = '';

  @ViewChildren('markerElement') markerElements!: QueryList<ElementRef>;
  @ViewChild('garageDetailsDialog') garageDetailsDialog?: ArivoBottomSheetDialogComponent;
  selectedMapMarker?: MapMarker;

  userLocation?: LatLng;
  centerMapToUserLocation: boolean = false;
  displayUserLocationWarning: boolean = false;
  signUpEnabled: boolean = false;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private _router: Router,
    private _urlPipe: UrlPipe,
    private _decimalPipe: DecimalPipe,
    private languageService: LanguageService,
    private featureFlagService: FeatureFlagService,
    @Inject(ActivatedRoute) private route: TypedRoute<ProductsResolve>,
  ) {}

  ngOnInit(): void {
    this.signUpEnabled = this.featureFlagService.getFeatureFlag('enable_signup');
    this.initializeMap();

    this.route.queryParams.subscribe((params) => {
      if (!params['garageId']) {
        this.updateSelectedMarker();
      }
    });

    if (this.selectedMapMarker) {
      this.map?.panTo([this.selectedMapMarker.location.latitude, this.selectedMapMarker.location.longitude]);
    }
  }

  //Unsure if this is really needed, but also cant hurt to provide a cleanup method
  ngOnDestroy(): void {
    // Clean up map markers
    this.mapMarkers.forEach((marker) => {
      if (this.map && marker.marker) {
        this.map.removeLayer(marker.marker);
      }
    });

    // Clean up map instance
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  //This is missing an try catch block, but i dont yet know how to handle a failed map initialization
  initializeMap(): void {
    this.map = L.map('map', {
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      minZoom: 3,
      zoomControl: false,
    }).setView([48.21155881631771, 16.365026225916996], 8);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a rel="noopener noreferrer" target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      detectRetina: true,
    }).addTo(this.map);

    // HACK: The map may sometimes not render completely. This workaround forces the map to render correctly.
    setTimeout(() => {
      this.map?.invalidateSize();
    });

    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {

      this.mapMarkers = data.productsApiResponse.garages.map((garage: Garage) => {
        const marker = L.marker([garage.location.latitude, garage.location.longitude], {
          icon: L.icon({
            iconUrl: this._urlPipe.transform('/assets/mapmarkers/marker-blue.svg'),
            iconSize: [28, 42],
            iconAnchor: [14, 42],
            popupAnchor: [0, -42],
          }),
        });

        const mapMarker = {
          ...garage,
          marker: marker,
        };

        marker.on('click', () => {
          this.updateSelectedMarker(mapMarker);
        });

        if (this.map) {
          marker.addTo(this.map);
        }

        const garageId = this.route.snapshot.queryParams['garageId'];
        if (garageId === mapMarker.id) {
          this.updateSelectedMarker(mapMarker);
        }

        return mapMarker;
      });

      this.filterMarkers();

      if (this.mapMarkers.length === 0) {
      }

      //TODO: figure out what to do with map options?
      let zoom = 14;

      // Use map center from backend if available 
      if (data.productsApiResponse.map_options?.center) {
        this.map?.setView(
          [data.productsApiResponse.map_options.center.latitude, data.productsApiResponse.map_options.center.longitude],
          zoom,
        );
      } else if (this.mapMarkers.length !== 0){
        // Otherwise try to fit all markers on the map
        var markers: L.Marker[] = this.mapMarkers.map((m) => m.marker);
        var group: L.FeatureGroup = L.featureGroup(markers);
        this.map?.fitBounds(group.getBounds(), {animate: false});
      } else {
        // Fallback to Graz center with default zoom
        this.changeDisplay('list');
        let grazLat = 47.0420163;
        let grazLng = 15.4389372;
        this.map?.setView([grazLat, grazLng], zoom);
      }
    });
  }

  updateSelectedMarker(marker?: MapMarker, panToMarker: boolean = false): void {
    if (this.selectedMapMarker) {
      this.selectedMapMarker.marker.setIcon(
        L.icon({
          iconUrl: this._urlPipe.transform('/assets/mapmarkers/marker-blue.svg'),
          iconSize: [28, 42],
          iconAnchor: [14, 42],
          popupAnchor: [0, -42],
        }),
      );
    }

    if (marker === this.selectedMapMarker) {
      this.resetSelectedMarker();
      return;
    }

    if (marker) {
      marker.marker.setIcon(
        L.icon({
          iconUrl: this._urlPipe.transform('/assets/mapmarkers/marker-blue.svg'),
          shadowUrl: this._urlPipe.transform('/assets/mapmarkers/marker-blue-selected-shadow.svg'),
          iconSize: [28, 42],
          shadowSize: [72, 72],
          iconAnchor: [14, 42],
          shadowAnchor: [36, 58],
          popupAnchor: [0, -42],
        }),
      );

      if (panToMarker) {
        this.map?.panTo([marker.location.latitude, marker.location.longitude]);
      }

      // Scroll the selected marker into view
      requestAnimationFrame(() => {
        const element = this.markerElements.find((el) => el.nativeElement.getAttribute('data-id') === marker.id.toString())?.nativeElement;
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      if (this.garageDetailsDialog) {
        this.garageDetailsDialog.open = true;
      }

      this._router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          garageId: marker.id,
        },
        queryParamsHandling: 'merge',
      });

      this.selectedMapMarker = marker;
    } else {
      this.resetSelectedMarker();
    }
  }

  changeDisplay(display: 'map' | 'list'): void {
    this.display = display;

    // HACK: The map may sometimes not render completely. This workaround forces the map to render correctly.
    if (this.display === 'map') {
      setTimeout(() => {
        this.map?.invalidateSize();
      });
    }
  }

  distanceToUser(mapMarker: MapMarker): number | undefined {
    if (this.userLocation) {
      return this.computeDistanceBetween(this.userLocation, {
        lat: mapMarker.location.latitude,
        lng: mapMarker.location.longitude,
      });
    } else {
      return undefined;
    }
  }

  formatAddress(garage: Garage): string {
    let formattedStreet = garage.street + (garage.number ? ' ' + garage.number : '');
    return `${formattedStreet}, ${garage.zip_code} ${garage.city}`;
  }

  formatDistance(distance?: number): string | undefined {
    if (distance) {
      if (distance >= 1000) {
        return `${this._decimalPipe.transform(Math.round(distance / 100) / 10)} km`;
      } else {
        return `${this._decimalPipe.transform(Math.round(distance))} m`;
      }
    } else {
      return undefined;
    }
  }

  computeDistanceBetween(a: LatLng, b: LatLng): number {
    const r = 6378137;

    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;

    const x = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));

    return r * c;
  }

  resetSelectedMarker() {
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        garageId: null,
      },
      queryParamsHandling: 'merge',
    });

    this.selectedMapMarker = undefined;
  }

  filterMarkers(): void {
    if (!this.mapMarkers || this.mapMarkers.length === 0 || this.markerFilter.trim() === '') {
      this.filteredMapMarkers = this.mapMarkers;
      return;
    }
    this.filteredMapMarkers = this.mapMarkers.filter((marker) => {
      return (
        marker.name.toLowerCase().includes(this.markerFilter.toLowerCase()) ||
        (marker.street.toLowerCase() + (marker.number ? ' ' + marker.number.toLowerCase() : '')).includes(this.markerFilter.toLowerCase())
      );
    });
  }

  clearMarkerFilter() {
    this.markerFilter = '';
    this.filterMarkers();
  }

  getCurrentLanguage(): string {
    return this.languageService.getCurrentLanguage();
  }
  changeLanguage(language: Language): void {
    this.languageService.setLanguage(language);
  }
}
